import type { Cell, ColumnDef } from '@tanstack/react-table';
import type { MouseEvent, ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ContextMenu } from 'ts/components/ContextMenu';
import type { TableCellProps } from 'ts/components/Table';
import { createStore, type StoreApi, useStore } from 'zustand';

/** Event callback that handles showing the context menu after a corresponding mouse event. */
export type CellContextMenuHandler<T> = (e: MouseEvent, selectedItems: T[], cell: Cell<T, unknown>) => void;

/** Takes a context menu definition and renders it. */
type ShowContextMenu = (e: React.MouseEvent, menuItems: ReactNode) => void;

/**
 * Helper hook for context menus that provides a onContextMenuHandler that can be applied on the table rows as well as a
 * factory function that creates the actual context menu.
 */
export function useContextMenu(): {
	showContextMenu: ShowContextMenu;
	contextMenu: ReactNode;
} {
	const [menuItemsStore] = useState(() => createStore<{ menuItems?: ReactNode }>(() => ({})));
	const actionsRef = useRef<ContextMenuActions | null>(null);
	const showContextMenu: ShowContextMenu = useCallback(
		(e, menuItems) => {
			actionsRef.current?.handleContextMenu?.(e);
			menuItemsStore.setState({ menuItems });
		},
		[menuItemsStore]
	);

	return {
		showContextMenu,
		contextMenu: <ContextMenuWrapper actionsRef={actionsRef} menuItemsStore={menuItemsStore} />
	};
}

function ContextMenuWrapper({
	actionsRef,
	menuItemsStore
}: {
	actionsRef: RefObject<ContextMenuActions | null>;
	menuItemsStore: StoreApi<{ menuItems?: ReactNode }>;
}) {
	const { menuItems } = useStore(menuItemsStore);
	return <ContextMenu trigger={<ExternalContextMenuTrigger actionsRef={actionsRef} />}>{menuItems}</ContextMenu>;
}

/** Actions interface for programmatically triggering context menu events from external components. */
type ContextMenuActions = {
	/** Function to handle context menu events programmatically. */
	handleContextMenu?: (event: MouseEvent) => void;
};

/**
 * A trigger component for context menus that attaches to external DOM elements via refs. Useful when you need to
 * trigger context menus on elements that are not direct children of the ContextMenu component or when you need
 * programmatic control over the trigger.
 */
function ExternalContextMenuTrigger({
	actionsRef,
	onContextMenu
}: {
	actionsRef: RefObject<ContextMenuActions | null>;
	onContextMenu?: (event: MouseEvent) => void;
}) {
	useEffect(() => {
		actionsRef.current = { handleContextMenu: onContextMenu };
	}, [actionsRef, onContextMenu]);
	return null;
}

/** Attaches the context menu handler to all columns. */
export function attachContextMenuHandler<T extends object>(
	columns: Array<ColumnDef<T>>,
	onContextMenu: CellContextMenuHandler<T>
): Array<ColumnDef<T>> {
	return columns.map(
		column =>
			({
				...column,

				meta: {
					...column.meta,
					getCellProps(cell) {
						let props: TableCellProps = {};
						const meta = column.meta;
						if (meta && 'getCellProps' in meta && meta.getCellProps) {
							props = meta.getCellProps(cell);
						}
						return {
							...props,
							onContextMenu(e: MouseEvent) {
								const rowModel = cell.getContext().table.getSelectedRowModel();
								const selectedItems = rowModel.flatRows.map(row => row.original);
								// In case the user right-clicked on a row without ticking the checkbox
								if (!cell.row.getIsSelected()) {
									cell.row.toggleSelected(true);
									selectedItems.push(cell.row.original);
								}
								onContextMenu(e, selectedItems, cell);
							}
						};
					}
				}
			}) as ColumnDef<T>
	);
}
